import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import {
  InvitationResponse,
  SupplierRegistrationResendResult,
  SupplierRequest,
  SupplierResponse,
  SuppliersPaginatedResponse,
  SupplierUpdateRequest,
} from "../types";
import { queryKeys } from "./queryKeys";

const onInviteSupplier = async ({
  emails,
  materialId,
}: {
  emails: string;
  materialId: string;
}) => {
  const { data } = await axios.get(
    `/user/invitation/material/${emails}/${materialId}`
  );
  return data;
};

export const useInviteSupplier = (
  onSuccess: (res: any) => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onInviteSupplier,
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.INVITED_MATERIAL_SUPPLIERS],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MATERIAL_OFFERS],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MATERIAL_COMPANIES_OPPORTUNITIES_BY_COUNTRY],
      });
      onSuccess(res);
    },
    onError,
  });
};

const onGetInvitedMaterialSuppliers = async (materialId: string) => {
  const { data } = await axios.get(`/user/invitations/material/${materialId}`);
  return data;
};

export const useInvitedMaterialSuppliers = (materialId: string) =>
  useQuery<InvitationResponse[]>({
    queryKey: [queryKeys.INVITED_MATERIAL_SUPPLIERS, materialId],
    queryFn: () => onGetInvitedMaterialSuppliers(materialId),
    enabled: !!materialId,
  });

const onGetInvitedOrderSuppliers = async (orderId: string) => {
  const { data } = await axios.get(`/user/invitations/order/${orderId}`);
  return data;
};

export const useInvitedOrderSuppliers = (orderId: string) =>
  useQuery({
    queryKey: [queryKeys.INVITED_ORDER_SUPPLIERS, orderId],
    queryFn: () => onGetInvitedOrderSuppliers(orderId),
    enabled: !!orderId,
  });

const onInviteOrderSupplier = async ({
  emails,
  orderId,
}: {
  emails: string;
  orderId: string;
}) => {
  const { data } = await axios.get(
    `/user/invitation/order/${emails}/${orderId}`
  );
  return data;
};

export const useInviteOrderSupplier = (
  onSuccess: (res: any) => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onInviteOrderSupplier,
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.INVITED_ORDER_SUPPLIERS],
      });
      onSuccess(res);
    },
    onError,
  });
};

const onGetInvitedSuppliers = async (materialIds: string[]) => {
  const { data } = await axios.post(`/user/invitations/materials`, materialIds);
  return data;
};

export const useInvitedSuppliers = (
  onSuccess?: (res: InvitationResponse[]) => void,
  onError?: () => void
) =>
  useMutation({
    mutationFn: onGetInvitedSuppliers,
    onSuccess,
    onError,
  });

type PotentialSuppliersProps = {
  countryId: string;
  page: number;
  materialIds: string[];
};

const onGetPotentialSuppliers = async ({
  countryId,
  page,
  materialIds,
}: PotentialSuppliersProps) => {
  const { data } = await axios.post(
    `/user/material/id/opportunities/country/${countryId}/${page}`,
    materialIds
  );
  return data;
};

export const usePotentialSuppliers = () =>
  useMutation({
    mutationFn: onGetPotentialSuppliers,
  });

const onGetSuppliers = async () => {
  const { data } = await axios.get("/suppliers");
  return data;
};

export const useSuppliers = () =>
  useQuery<SuppliersPaginatedResponse>({
    queryKey: [queryKeys.SUPPLIERS],
    queryFn: onGetSuppliers,
  });

const onGetSuppliersByBuyer = async (
  page: number,
  query: string,
  size = 10
) => {
  const { data } = await axios.get(
    `/suppliers/by-buyer?page=${page}&query=${query}&size=${size}`
  );
  return data;
};

export const useSuppliersByBuyer = (
  page = 0,
  query: string,
  options?: { enabled?: boolean },
  size = 10
) =>
  useQuery<SuppliersPaginatedResponse>({
    queryKey: [queryKeys.SUPPLIERS_BY_BUYER, page, query, size],
    queryFn: () => onGetSuppliersByBuyer(page, query, size),
    enabled: options?.enabled ?? true,
  });

const onResendSupplierInvitation = async (
  supplierId: string
): Promise<SupplierRegistrationResendResult> => {
  const { data } = await axios.post(
    `/suppliers/${supplierId}/registration-invitation/resend`
  );
  return data;
};

const onResendSupplierInvitations = async ({
  supplierIds,
  dryRun = false,
}: {
  supplierIds?: string[];
  dryRun?: boolean;
}): Promise<SupplierRegistrationResendResult> => {
  const { data } = await axios.post(
    `/suppliers/registration-invitations/resend`,
    {
      supplierIds: supplierIds ?? [],
      dryRun,
    }
  );
  return data;
};

/** Re-sends the registration invitation to one supplier that never registered. */
export const useResendSupplierInvitation = (
  onSuccess: (result: SupplierRegistrationResendResult) => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onResendSupplierInvitation,
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUPPLIERS_BY_BUYER],
      });
      onSuccess(result);
    },
    onError,
  });
};

/** Bulk form: the given suppliers, or — with none — every pending supplier of the company. */
export const useResendSupplierInvitations = (
  onSuccess: (result: SupplierRegistrationResendResult) => void,
  onError: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (supplierIds?: string[]) =>
      onResendSupplierInvitations({ supplierIds }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUPPLIERS_BY_BUYER],
      });
      onSuccess(result);
    },
    onError,
  });
};

/** A dry run of the bulk resend — who would get it and who would be skipped — for the
 * confirmation dialog's count. Sends nothing. */
export const useSupplierInvitationResendPreview = (
  supplierIds: string[],
  enabled: boolean
) =>
  useQuery<SupplierRegistrationResendResult>({
    queryKey: [queryKeys.SUPPLIER_INVITATION_RESEND_PREVIEW, supplierIds],
    queryFn: () => onResendSupplierInvitations({ supplierIds, dryRun: true }),
    enabled,
    gcTime: 0,
  });

const onGetSupplierDetail = async (id: string | undefined) => {
  if (!id) throw new Error("No supplier id provided");
  const { data } = await axios.get(`/suppliers/${id}`);
  return data;
};

export const useSupplier = (id?: string) =>
  useQuery<SupplierResponse>({
    queryKey: [queryKeys.SUPPLIER, id],
    queryFn: () => onGetSupplierDetail(id),
    enabled: !!id,
  });

const onUploadSupplier = async (body: SupplierRequest) => {
  const { data } = await axios.post("/suppliers/upload", body);
  return data;
};

export const useUploadSupplier = () =>
  useMutation({
    mutationFn: onUploadSupplier,
  });

const searchSuppliers = async ({
  query,
  page = 0,
}: {
  query: string;
  page: number;
}) => {
  const { data } = await axios.post(`/user/name/${query}/${page}`);
  return data;
};

export const useSearchSuppliers = (
  onSuccess?: () => void,
  onError?: () => void
) => {
  return useMutation({
    mutationFn: searchSuppliers,
    onSuccess,
    onError,
  });
};

const onDownloadSuppliersTemplate = async () => {
  const response = await axios.get("/suppliers/template", {
    responseType: "blob",
  });
  return response;
};

export const useDownloadSuppliersTemplate = () => {
  return useMutation({
    mutationFn: onDownloadSuppliersTemplate,
  });
};

const onUploadSupplierExcel = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post("/suppliers/upload/excel", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};

export const useUploadSupplierExcel = (
  onSuccess: (res: any) => void,
  onError: (error: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: onUploadSupplierExcel,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.SUPPLIERS] });
      onSuccess(res);
    },
    onError,
  });
};

const updateNewSupplier = async (body: SupplierUpdateRequest) => {
  const { data } = await axios.put(`/user/pending-registration`, body);
  return data;
};

export const useUpdateNewSupplier = (onSuccess: (res: any) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNewSupplier,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.SUPPLIER] });
      onSuccess(res);
    },
  });
};
